import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  RefreshCw,
  Play,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  FileSpreadsheet,
  Clock,
  ExternalLink,
} from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";

// Turkish stage labels for the preview table — mirror STAGE_MAP on the backend.
const STAGE_LABEL = {
  yeni_gorusme: "Yeni Görüşme",
  iletisimde: "İletişimde",
  teklif_verildi: "Teklif Verildi",
  muzakere: "Müzakere",
  basvuru_alindi: "Başvuru Alındı",
  kazanildi: "Kazanıldı",
  kaybedildi: "Kaybedildi",
};

const PRIORITY_LABEL = { high: "Yüksek", medium: "Orta", low: "Düşük" };

// Compact date formatter — accepts ISO string, returns "d MMM yyyy HH:mm".
function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function SmartsheetIntegration() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheetRef, setSheetRef] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/smartsheet/status");
      setStatus(data);
      setSheetRef(data?.state?.sheet_ref || "");
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Smartsheet durumu okunamadı"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onPreview = async () => {
    setPreviewing(true);
    setPreviewData(null);
    try {
      const { data } = await api.post("/smartsheet/preview", {
        sheet_ref: sheetRef,
        limit: 20,
      });
      setPreviewData(data);
      toast.success(`${data.total_rows} satırlık sheet bulundu`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ön izleme başarısız");
    } finally {
      setPreviewing(false);
    }
  };

  const onSync = async () => {
    if (
      !window.confirm(
        `Tüm sheet (${previewData?.total_rows ?? "—"} satır) içe aktarılacak. Devam edilsin mi?`
      )
    )
      return;
    setSyncing(true);
    try {
      const { data } = await api.post("/smartsheet/sync", {
        sheet_ref: sheetRef,
      });
      const r = data.report || {};
      toast.success(
        `İçe aktarım tamam · ${r.deals_created ?? 0} yeni, ${r.deals_updated ?? 0} güncellenen`
      );
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Senkron başarısız");
    } finally {
      setSyncing(false);
    }
  };

  const onSaveCfg = async (partial) => {
    setSavingCfg(true);
    try {
      const { data } = await api.put("/smartsheet/config", partial);
      setStatus((s) => ({ ...s, state: data.state }));
      toast.success("Ayar kaydedildi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSavingCfg(false);
    }
  };

  if (loading) {
    return (
      <section className="rounded-2xl border border-zinc-200 bg-white p-6">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
      </section>
    );
  }

  const state = status?.state || {};
  const report = state.last_sync_report;

  return (
    <section
      data-testid="smartsheet-integration-panel"
      className="rounded-2xl border border-zinc-200 bg-white p-6"
    >
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-100 to-sky-100">
          <FileSpreadsheet className="h-4 w-4 text-emerald-700" />
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-zinc-950">Smartsheet Entegrasyonu</h2>
            <span
              data-testid="smartsheet-readonly-badge"
              title="Serpix Smartsheet'e asla yazmaz — sadece okuma yapar."
              className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 ring-1 ring-emerald-200"
            >
              READ-ONLY · Smartsheet → Serpix
            </span>
          </div>
          <p className="mt-0.5 text-[12px] text-zinc-500">
            Sales pipeline&apos;ını Smartsheet&apos;ten Serpix&apos;e senkronize et.{" "}
            <span className="text-zinc-400">
              Serpix&apos;ten Smartsheet&apos;e veri gitmez.
            </span>
          </p>
        </div>
      </div>

      {!status?.token_configured && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-semibold">API token yapılandırılmamış.</div>
            <div className="mt-0.5">
              <code className="rounded bg-amber-100 px-1">SMARTSHEET_ACCESS_TOKEN</code> ortam değişkenini
              backend&apos;e ekleyin.
            </div>
          </div>
        </div>
      )}

      {/* Sheet reference input + save */}
      <div className="mb-4">
        <Label className="text-[11px] uppercase tracking-wider text-zinc-500">
          Sheet URL veya ID
        </Label>
        <div className="mt-1 flex gap-2">
          <Input
            data-testid="smartsheet-sheet-ref-input"
            value={sheetRef}
            onChange={(e) => setSheetRef(e.target.value)}
            placeholder="https://app.smartsheet.com/sheets/... veya numeric id"
            className="flex-1"
          />
          <Button
            variant="outline"
            data-testid="smartsheet-save-ref-btn"
            disabled={savingCfg || sheetRef === state.sheet_ref}
            onClick={() => onSaveCfg({ sheet_ref: sheetRef })}
          >
            Kaydet
          </Button>
        </div>
        {state.sheet_name && (
          <p className="mt-1 text-[11px] text-zinc-500">
            Şu anki sheet: <span className="font-semibold text-zinc-800">{state.sheet_name}</span>{" "}
            <span className="text-zinc-400">(id={state.sheet_id})</span>
          </p>
        )}
      </div>

      {/* Auto sync toggle */}
      <div className="mb-4 flex items-center justify-between rounded-xl border border-zinc-200 bg-zinc-50 p-3">
        <div>
          <div className="text-[12px] font-semibold text-zinc-900">Saatlik otomatik senkron</div>
          <div className="text-[11px] text-zinc-500">
            Aktifken her saat başında Smartsheet&apos;ten değişiklikler alınır.
          </div>
        </div>
        <Switch
          data-testid="smartsheet-auto-sync-toggle"
          checked={!!state.auto_sync_enabled}
          onCheckedChange={(v) => onSaveCfg({ auto_sync_enabled: v })}
          disabled={savingCfg}
        />
      </div>

      {/* Last sync info */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatBox
          icon={Clock}
          label="Son sync"
          value={state.last_sync_at ? fmtDT(state.last_sync_at) : "Henüz yok"}
        />
        <StatBox
          icon={CheckCircle2}
          label="İçe aktarılmış"
          value={`${status?.imported_deals ?? 0} deal`}
        />
        <StatBox
          icon={Sparkles}
          label="Son eklenen"
          value={`${report?.deals_created ?? 0}`}
          tone="emerald"
        />
        <StatBox
          icon={RefreshCw}
          label="Son güncellenen"
          value={`${report?.deals_updated ?? 0}`}
          tone="sky"
        />
      </div>

      {state.last_error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-[12px] text-rose-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-semibold">Son hata</div>
            <div className="mt-0.5 font-mono text-[11px]">{state.last_error}</div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <Button
          data-testid="smartsheet-preview-btn"
          onClick={onPreview}
          disabled={previewing || !sheetRef}
          variant="outline"
          className="gap-1"
        >
          {previewing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Play className="h-4 w-4" />
          )}
          İlk 20 satırı önizle
        </Button>
        <Button
          data-testid="smartsheet-sync-btn"
          onClick={onSync}
          disabled={syncing || !previewData}
          className="gap-1 bg-emerald-600 text-white hover:bg-emerald-700"
        >
          {syncing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Şimdi tam sync et
        </Button>
        {previewData && !syncing && (
          <span className="ml-auto flex items-center gap-1 text-[11px] text-zinc-500">
            <CheckCircle2 className="h-3 w-3 text-emerald-500" />
            {previewData.sample.length} örnek yüklendi — sync&apos;e hazır
          </span>
        )}
      </div>

      {/* Preview table */}
      {previewData && (
        <div className="mt-4 overflow-x-auto rounded-xl border border-zinc-200">
          <table className="w-full min-w-[720px] text-[12px]" data-testid="smartsheet-preview-table">
            <thead className="bg-zinc-50">
              <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Firma</th>
                <th className="px-3 py-2">Kişi</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Ülke / Sektör</th>
                <th className="px-3 py-2">Stage</th>
                <th className="px-3 py-2">Priority</th>
                <th className="px-3 py-2">Sorumlu</th>
              </tr>
            </thead>
            <tbody>
              {previewData.sample.map((p) => (
                <tr
                  key={p.smartsheet_row_id}
                  data-testid={`smartsheet-preview-row-${p.smartsheet_row_id}`}
                  className="border-t border-zinc-100 hover:bg-zinc-50"
                >
                  <td className="px-3 py-2 text-zinc-400 tabular-nums">
                    {p.smartsheet_row_number}
                  </td>
                  <td className="px-3 py-2 font-medium text-zinc-900">{p.company_name}</td>
                  <td className="px-3 py-2 text-zinc-700">{p.contact_name}</td>
                  <td className="px-3 py-2 text-zinc-500">{p.email}</td>
                  <td className="px-3 py-2 text-zinc-700">
                    {[p.country, p.sector].filter(Boolean).join(" · ")}
                  </td>
                  <td className="px-3 py-2">
                    <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-800">
                      {STAGE_LABEL[p.stage_key] || p.stage_key}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                        p.priority === "high"
                          ? "bg-rose-100 text-rose-800"
                          : p.priority === "medium"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-emerald-100 text-emerald-800"
                      }`}
                    >
                      {PRIORITY_LABEL[p.priority] || p.priority}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-zinc-700">{p.assignee_display}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-zinc-100 bg-zinc-50 px-3 py-2 text-[11px] text-zinc-600">
            Toplam {previewData.total_rows} satır. Yukarıdaki {previewData.sample.length} örnek doğru
            gözüküyorsa <span className="font-semibold">&quot;Şimdi tam sync et&quot;</span> ile
            hepsi içe aktarılır.
          </div>
        </div>
      )}

      {/* Report */}
      {report && (
        <div
          data-testid="smartsheet-last-report"
          className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 text-[12px]"
        >
          <div className="mb-2 flex items-center gap-1.5 font-semibold text-emerald-900">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Son sync raporu
          </div>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
            <li>Toplam satır: <strong>{report.total_rows}</strong></li>
            <li>Yeni deal: <strong>{report.deals_created}</strong></li>
            <li>Güncellenen: <strong>{report.deals_updated}</strong></li>
            <li>Boş firma (atlandı): <strong>{report.skipped_no_company}</strong></li>
            <li>Yeni firma kartı: <strong>{report.companies_created}</strong></li>
            <li>Yeni kişi kartı: <strong>{report.persons_created}</strong></li>
            <li>Sorumlu eşleşen: <strong>{report.assignee_matched}</strong></li>
            <li>Sorumlu eşleşmeyen: <strong>{report.assignee_unmatched}</strong></li>
          </ul>
        </div>
      )}

      <div className="mt-4 border-t border-zinc-100 pt-3 text-[11px] text-zinc-500">
        <div className="flex items-center gap-1">
          <ExternalLink className="h-3 w-3" />
          <a
            href="https://app.smartsheet.com/sheets/jWm3HhxRfrJCr8Wm53RrxcjpvvMRxQwWCqQ5XGx1"
            target="_blank"
            rel="noreferrer"
            className="hover:text-violet-700 hover:underline"
          >
            Smartsheet&apos;te sheet&apos;i aç
          </a>
        </div>
      </div>
    </section>
  );
}

function StatBox({ icon: Icon, label, value, tone }) {
  const toneClasses =
    tone === "emerald"
      ? "border-emerald-200 bg-emerald-50/70 text-emerald-900"
      : tone === "sky"
        ? "border-sky-200 bg-sky-50/70 text-sky-900"
        : "border-zinc-200 bg-white text-zinc-900";
  return (
    <div className={`rounded-xl border p-2 ${toneClasses}`}>
      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider opacity-70">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-0.5 text-[13px] font-semibold tabular-nums">{value}</div>
    </div>
  );
}
